/**
 * Plagiarism text matching utility using n-gram algorithm.
 * Used by both PlagiarismModal and plagiarismStore for consistent highlighting.
 */

// ============================================================================
// Types
// ============================================================================

export interface WordToken {
  word: string;
  normalized: string;
  start: number;
  end: number;
}

export interface HighlightRange {
  start: number;
  end: number;
}

// ============================================================================
// Constants
// ============================================================================

const MIN_MATCHING_WORDS = 4;
const NGRAM_SEPARATOR = '|';
const MAX_GAP = 4;
const MAX_GAP_WORDS = 8;

// ============================================================================
// Text Processing Utilities
// ============================================================================

/**
 * Strips trailing citation markers from a word (e.g., "corm.[3]" -> "corm.")
 * Handles patterns like word.[1], word,[2], word[3], etc.
 */
const stripTrailingCitation = (word: string): string => {
  return word.replace(/[.,;:]?\[\d+\]$/g, '');
};

/**
 * Normalizes a word for comparison by:
 * 1. Stripping trailing citation markers
 * 2. Converting to lowercase
 * 3. Removing punctuation but keeping letters and numbers
 * Supports all Unicode scripts (Latin, Cyrillic, Arabic, etc.) and digits.
 */
const normalizeWord = (word: string): string => {
  const stripped = stripTrailingCitation(word);
  return stripped.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
};

/**
 * Checks if a token is ONLY a citation marker (e.g., [2], [10], etc.)
 * These should be excluded from matching to avoid false positives.
 */
const isCitationMarker = (word: string): boolean => {
  return /^\[\d+\]$/.test(word.trim());
};

/**
 * Tokenizes text into words while preserving their original positions.
 * Each token contains the original word, its normalized form, and character positions.
 * Citation markers (e.g., [2], [10]) are excluded from matching tokens.
 */
export const tokenize = (text: string): WordToken[] => {
  const tokens: WordToken[] = [];
  const wordRegex = /(?:[^\W\d_\u0600-\u06FF]+|[\u0600-\u06FF]+)[.;?!]?|\d+(?:\.\d+)?[.;?!]?|\d+(?:[^\W\d_]+|[\u0600-\u06FF]+)[.;?!]?/g;
  let match: RegExpExecArray | null;

  while ((match = wordRegex.exec(text)) !== null) {
    const word = match[0];

    // Skip citation markers - they should not be part of matching
    if (isCitationMarker(word)) {
      continue;
    }

    const normalized = normalizeWord(word);

    // Only include tokens with meaningful content (letters or numbers)
    if (normalized.length > 0) {
      tokens.push({
        word,
        normalized,
        start: match.index,
        end: match.index + word.length,
      });
    }
  }

  return tokens;
};

/**
 * Creates a Set of n-grams from tokenized text.
 * Each n-gram is a string of n consecutive normalized words joined by separator.
 */
export const createNgramSet = (tokens: WordToken[], n: number = MIN_MATCHING_WORDS): Set<string> => {
  const ngrams = new Set<string>();

  for (let i = 0; i <= tokens.length - n; i++) {
    const ngram = tokens
      .slice(i, i + n)
      .map(t => t.normalized)
      .join(NGRAM_SEPARATOR);
    ngrams.add(ngram);
  }

  return ngrams;
};

/**
 * Creates a unified n-gram set from multiple texts.
 * Processes each text separately to avoid boundary issues.
 */
export const createNgramSetFromTexts = (texts: string[], n: number = MIN_MATCHING_WORDS): Set<string> => {
  const allNgrams = new Set<string>();

  for (const text of texts) {
    const tokens = tokenize(text);
    const ngrams = createNgramSet(tokens, n);
    ngrams.forEach(ngram => allNgrams.add(ngram));
  }

  return allNgrams;
};

/**
 * Finds token indices in text that match n-grams from the comparison set.
 */
export const findMatchingIndices = (
  tokens: WordToken[],
  comparisonNgrams: Set<string>,
  n: number = MIN_MATCHING_WORDS
): Set<number> => {
  const matchedIndices = new Set<number>();

  for (let i = 0; i <= tokens.length - n; i++) {
    const ngram = tokens
      .slice(i, i + n)
      .map(t => t.normalized)
      .join(NGRAM_SEPARATOR);

    if (comparisonNgrams.has(ngram)) {
      for (let j = i; j < i + n; j++) {
        matchedIndices.add(j);
      }
    }
  }

  return matchedIndices;
};

/**
 * Checks if there's a sentence boundary (., ?, !, ;, :) in the given text range.
 */
const hasSentenceBoundary = (text: string, start: number, end: number): boolean => {
  const substring = text.substring(start, end);
  return /[.!?;:]/.test(substring);
};

/**
 * Merges overlapping or adjacent ranges into continuous spans.
 * Merges ranges that are within a small gap (to handle spaces/punctuation between words).
 * Does NOT merge ranges that span across different sentences.
 * Returns sorted, non-overlapping ranges.
 */
export const mergeRanges = (ranges: HighlightRange[], text: string): HighlightRange[] => {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: HighlightRange[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    // Check if overlapping or within MAX_GAP characters
    if (current.start <= last.end + MAX_GAP) {
      // Only merge if there's no sentence boundary between the ranges
      const gapStart = last.end - 1;
      const gapEnd = current.start;
      
      if (!hasSentenceBoundary(text, gapStart, gapEnd)) {
        last.end = Math.max(last.end, current.end);
      } else {
        // Sentence boundary between ranges — don't merge them.
        // But if the gap is short (≤ 5 chars) and contains no letters,
        // absorb it into the left range so the sentence-ending punctuation
        // stays highlighted together with the preceding match.
        const gap = text.substring(last.end, current.start);
        if (gap.length <= 5 && !/\p{L}/u.test(gap)) {
          last.end = current.start;
        }
        merged.push(current);
      }
    } else {
      merged.push(current);
    }
  }
  return merged;
};

/**
 * Converts matched token indices to character ranges.
 */
export const indicesToRanges = (tokens: WordToken[], matchedIndices: Set<number>): HighlightRange[] => {
  const ranges: HighlightRange[] = [];

  matchedIndices.forEach(idx => {
    if (tokens[idx]) {
      ranges.push({
        start: tokens[idx].start,
        end: tokens[idx].end,
      });
    }
  });

  return ranges;
};

/**
 * Finds character ranges in text that match n-grams from the comparison set.
 * Returns merged, non-overlapping ranges sorted by start position.
 */
export const findMatchingRanges = (
  tokens: WordToken[],
  comparisonNgrams: Set<string>,
  text: string,
  n: number = MIN_MATCHING_WORDS
): HighlightRange[] => {
  const matchedIndices = findMatchingIndices(tokens, comparisonNgrams, n);
  const ranges = indicesToRanges(tokens, matchedIndices);
  return mergeRanges(ranges, text);
};

/**
 * Counts whitespace-delimited words in a text range.
 */
const countWordsInRange = (text: string, start: number, end: number): number => {
  const substring = text.slice(start, end).trim();
  if (substring.length === 0) return 0;
  return substring.split(/\s+/).length;
};

/**
 * Groups highlight ranges by word gap threshold.
 * If the gap between consecutive ranges exceeds maxGapWords, a new group starts.
 */
const groupRangesByWordGap = (
  ranges: HighlightRange[],
  text: string,
  maxGapWords: number = MAX_GAP_WORDS
): HighlightRange[][] => {
  if (ranges.length === 0) return [];

  const groups: HighlightRange[][] = [[ranges[0]]];

  for (let i = 1; i < ranges.length; i++) {
    const prev = ranges[i - 1];
    const curr = ranges[i];
    const gapWords = countWordsInRange(text, prev.end, curr.start);

    if (gapWords > maxGapWords) {
      groups.push([curr]);
    } else {
      groups[groups.length - 1].push(curr);
    }
  }

  return groups;
};

/**
 * Picks the group with the maximum total highlighted character length.
 */
const selectBestGroup = (ranges: HighlightRange[], text: string): HighlightRange[] => {
  const groups = groupRangesByWordGap(ranges, text);
  if (groups.length <= 1) return groups[0] || [];

  let best = groups[0];
  let bestLen = best.reduce((s, r) => s + (r.end - r.start), 0);

  for (let i = 1; i < groups.length; i++) {
    const len = groups[i].reduce((s, r) => s + (r.end - r.start), 0);
    if (len > bestLen) {
      bestLen = len;
      best = groups[i];
    }
  }

  return best;
};

/**
 * Filters ranges to only those overlapping with a given region,
 * clamping to region boundaries.
 */
const filterRangesToRegion = (
  ranges: HighlightRange[],
  region: HighlightRange
): HighlightRange[] => {
  return ranges
    .filter(r => r.start < region.end && r.end > region.start)
    .map(r => ({
      start: Math.max(r.start, region.start),
      end: Math.min(r.end, region.end),
    }));
};

/**
 * Computes highlight ranges for both original and match texts.
 * This is the main entry point for computing matching highlights between two texts.
 *
 * When textSentence is provided, original ranges are scoped to that sentence
 * and sentenceRange marks its position in originalText (for gray highlighting).
 */
export const computeTextHighlights = (
  originalText: string,
  matchText: string,
  textSentence?: string,
): { originalRanges: HighlightRange[]; matchRanges: HighlightRange[]; sentenceRange: HighlightRange | null } => {
  const originalTokens = tokenize(originalText);
  const matchTokens = tokenize(matchText);

  const matchNgrams = createNgramSet(matchTokens);

  let originalRanges = findMatchingRanges(originalTokens, matchNgrams, originalText);

  let sentenceRange: HighlightRange | null = null;
  if (textSentence) {
    const idx = originalText.indexOf(textSentence);
    if (idx !== -1) {
      sentenceRange = { start: idx, end: idx + textSentence.length };
      originalRanges = filterRangesToRegion(originalRanges, sentenceRange);
    }
  }

  originalRanges = selectBestGroup(originalRanges, originalText);

  // Build match-side n-grams only from the selected original ranges
  const selectedTexts = originalRanges.map(r => originalText.slice(r.start, r.end));
  const selectedNgrams = createNgramSetFromTexts(selectedTexts);
  const matchRanges = findMatchingRanges(matchTokens, selectedNgrams, matchText);

  return { originalRanges, matchRanges, sentenceRange };
};

/**
 * Finds highlight ranges in original text that match any of the plagiarized texts.
 * Used for highlighting in the main editor view.
 */
export const findPlagiarismRanges = (
  originalText: string,
  plagiarizedTexts: string[]
): HighlightRange[] => {
  const originalTokens = tokenize(originalText);
  console.log("originalTokens", originalTokens)
  const plagiarizedNgrams = createNgramSetFromTexts(plagiarizedTexts);
  return findMatchingRanges(originalTokens, plagiarizedNgrams, originalText);
};
